
import { Vue, Component, Mixins, Model, Emit, Watch } from "vue-property-decorator";
import { IContentSource } from "../../../../domain/drawing";
import { FixedPhraseRootReference, FixedPhraseReference } from "../../../../domain/document";
import { ManagementTokenPublishResult } from "../../../../domain/system";
import {
  ITreeExpandable,
  instanceOfITreeExpandable,
  ITreeInformation,
  instanceOfITreeInformation,
  ManagementTokenCollaborativeMixin,
} from "../../../system";
import { WindowOpener } from "../../../system/utilities";
import { FixedPhraseKinds, ISelectedFixedPhraseGettable } from "../";
import { FixedPhraseAccessorFactory, IPhraseAccessible } from "../accessors";
import { FixedPhraseEditDialogOptions } from "../dialogs";


@Component
export default class FixedPhraseTreeSelectorMixin extends Mixins(ManagementTokenCollaborativeMixin) implements ITreeExpandable, ITreeInformation, ISelectedFixedPhraseGettable {
  @Model("input")
  value: FixedPhraseReference;

  data(): any {
    return {
      rootFixedPhrase: null as FixedPhraseRootReference,  // ロード中判定のため初期値はnull.
      selectedFixedPhrase: null as FixedPhraseReference,
      selector: null as Vue | Element | Vue[] | Element[],
    };
  }
  rootFixedPhrase: FixedPhraseRootReference;
  selectedFixedPhrase: FixedPhraseReference;
  selector: Vue | Element | Vue[] | Element[];

  get isLoaded(): boolean {
    return (this.rootFixedPhrase !== null);
  }

  get selectedKey(): number {
    if (!this.isLoaded) {
      return null;
    }
    if (!instanceOfITreeInformation(this.selector)) {
      return null;
    }
    return this.selector.selectedKey;
  }

  @Watch("selectedFixedPhrase")
  onSelectedFixedPhraseUpdated(newValue: FixedPhraseReference, oldValue: FixedPhraseReference): void {
    this.input(newValue);
  }

  @Emit()
  input(value: FixedPhraseReference): any {}

  expandTree(): void {
    if (!this.isLoaded) {
      return;
    }
    if (!instanceOfITreeExpandable(this.selector)) {
      return;
    }
    this.selector.expandTree();
  }

  expandKeys(): number[] {
    if (!this.isLoaded) {
      return [];
    }
    if (!instanceOfITreeInformation(this.selector)) {
      return [];
    }
    return this.selector.expandKeys();
  }

  async getEditedFixedPhrase(latest: FixedPhraseRootReference, kind: FixedPhraseKinds, id: string, options?: FixedPhraseEditDialogOptions): Promise<FixedPhraseRootReference> {
    try {
      return await this.$commonDialogs.document.fixedPhraseEditor.show(this.rootFixedPhrase, this.expandKeys(), this.selectedKey, latest, kind, id, options);
    } catch (e) {
      this.$error(
        "定型文編集失敗",
        "定型文編集中にエラーが発生しました。",
        e);
      return null;
    }
  }

  private get fixedPhraseAccessorFactory(): FixedPhraseAccessorFactory {
    return new FixedPhraseAccessorFactory({
      department: this.$wording.common.departmentAsShort,
      note: this.$wording.document.note,
    });
  }

  private getFixedPhraseAccessor(key: FixedPhraseKinds): IPhraseAccessible {
    return this.fixedPhraseAccessorFactory.get(key);
  }

  async getFixedPhrase(kind: FixedPhraseKinds, id?: string): Promise<FixedPhraseRootReference> {
    const accessor = this.getFixedPhraseAccessor(kind);
    if (!accessor) { return; }
    try {
      return accessor.get(id);
    } catch (e) {
      this.$error(
        accessor.name + "取得失敗",
        accessor.name + "の取得に失敗しました。",
        e,
      );
    }
  }

  async setFixedPhrase(kind: FixedPhraseKinds, source: FixedPhraseRootReference, postProcess?: () => void): Promise<void> {
    const accessor = this.getFixedPhraseAccessor(kind);
    if (!accessor) { return; }
    try {
      const id = await accessor.set(source);
      this.rootFixedPhrase = source;
      this.rootFixedPhrase.tableId = id;
      if (postProcess) {
        postProcess();
      }
    } catch (e) {
      this.$error(
        accessor.name + "保存失敗",
        accessor.name + "を保存できませんでした。",
        e);
    }
  }

  async exportAndDownloadFixedPhrase(kind: FixedPhraseKinds, id: string, isJsonFile: boolean): Promise<void> {
    const accessor = this.getFixedPhraseAccessor(kind);
    if (!accessor) { return; }
    try {
      this.downloadContent(await accessor.export(isJsonFile, id));
    } catch (e) {
      this.$error(
        accessor.name + "エクスポート失敗",
        accessor.name + "のエクスポートに失敗しました。",
        e,
      );
    }
  }

  async downloadContent(source: IContentSource): Promise<void> {
    if (!source) { return; }
    window.open(WindowOpener.toUrl(source.source, {}));
  }

  async getManagementToken(kind: FixedPhraseKinds, isForcibly: boolean, id?: string): Promise<ManagementTokenPublishResult> {
    return this.getFixedPhraseAccessor(kind).getToken(isForcibly, id);
  }  

  getSelectedFixedPhrase(): FixedPhraseReference {
    return this.selectedFixedPhrase;
  }

  @Watch("rootFixedPhrase")
  onRootFixedPhraseUpdated() {
    if (this.isLoaded && this.$configuration.behavior.fixedPhrase.shouldExpandTreeAutomatically) {
      this.$nextTick(() => {
        this.expandTree();
      });
    }
  }
}
